import type { ChainedCommands } from '@tiptap/core';
import { findChildren, mergeAttributes } from '@tiptap/core';
import classNames from 'classnames';
import { Decoration_Type } from 'ricos-schema';
import type { DOMOutputSpec } from 'ricos-types';

import { createRicosMarkExtension } from '../create-ricos-extension';
import styles from '../statics/styles.scss';
import { ANCHOR_LINK } from './dataHooks';
import { cleanAndSetSelection, getSelectedMarkRangeByTypeNames } from './utils';

const SCROLL_OFFSET = 50;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    anchor: {
      /**
       * Set an anchor mark
       */
      setAnchor: (data: { anchor: string; defaultName: string }) => ReturnType;
      /**
       * Unset an anchor mark
       */
      unsetAnchor: () => ReturnType;
      /**
       * Scroll view to anchor
       */
      scrollToAnchor: (anchor: string) => ReturnType;
    };
  }
}

const scrollIntoViewWithOffset = (element: HTMLElement, offset: number) => {
  const { paddingTop, marginTop } = element.style;
  element.style.marginTop = `-${offset}px`;
  element.style.paddingTop = `${offset}px`;
  element.scrollIntoView({ behavior: 'smooth' });
  element.style.marginTop = marginTop;
  element.style.paddingTop = paddingTop;
};

export const anchor = createRicosMarkExtension<{
  HTMLAttributes: Record<string, string>;
  inclusive: boolean;
}>({
  type: 'mark',
  groups: [],
  name: Decoration_Type.ANCHOR,
  createExtensionConfig() {
    return {
      name: this.name,
      keepOnSplit: false,

      priority: 1000,

      addOptions: () => ({
        inclusive: false,
        HTMLAttributes: {},
      }),

      inclusive() {
        return this.options.inclusive;
      },

      addAttributes() {
        return {
          anchor: {
            default: '',
          },
        };
      },

      renderHTML({ HTMLAttributes }) {
        const { link, linkInViewer } = styles;
        const classes = classNames(link, linkInViewer);
        const anchorAttributes = HTMLAttributes.anchor
          ? {
              'ricos-anchor': HTMLAttributes.anchor,
              href: `#${HTMLAttributes.anchor}`,
              target: '_self',
            }
          : {};

        return [
          'a',
          mergeAttributes(this.options.HTMLAttributes, {
            ...anchorAttributes,
            class: classes,
            'data-hook': ANCHOR_LINK,
          }),
          0,
        ] as DOMOutputSpec;
      },

      parseHTML() {
        return [
          {
            tag: `a[data-hook~=${ANCHOR_LINK}][ricos-anchor]`,
            getAttrs: node => {
              const anchor = node.getAttribute('ricos-anchor') ?? '';
              return { anchor };
            },
          },
        ];
      },

      addCommands() {
        return {
          setAnchor:
            data =>
            ({ commands, state, tr, chain }) => {
              cleanAndSetSelection(
                this.name,
                Decoration_Type.LINK,
                commands.unsetLink,
                state.schema,
                tr.selection,
                state.doc,
                commands,
                data.defaultName
              );
              const setLinkChain = chain().setMark(this.name, { anchor: data.anchor });

              if ('setUnderline' in setLinkChain) {
                return ((setLinkChain.setUnderline as any)() as ChainedCommands).run();
              }

              return setLinkChain.run();
            },

          unsetAnchor:
            () =>
            ({ tr, state, chain }) => {
              const { from, to } = getSelectedMarkRangeByTypeNames(
                [this.name],
                state.schema,
                tr.selection
              );

              const unsetLinkChain = chain().setTextSelection({ from, to }).unsetMark(this.name);

              if ('unsetUnderline' in unsetLinkChain) {
                return ((unsetLinkChain.unsetUnderline as any)() as ChainedCommands).run();
              }

              return unsetLinkChain.run();
            },
          scrollToAnchor:
            anchor =>
            ({ editor }) => {
              const { view } = editor;
              const foundNodes = findChildren(view.state.doc, node => {
                return node?.attrs?.id?.toString() === anchor;
              });

              if (foundNodes.length === 0) {
                return false;
              }

              let targetDom: Node | null = null;

              targetDom = view.nodeDOM(foundNodes[0].pos);

              if (targetDom && targetDom instanceof HTMLElement && !targetDom.scrollIntoView) {
                // might be text node
                targetDom = targetDom.parentElement;
              }

              if (targetDom && targetDom instanceof HTMLElement) {
                scrollIntoViewWithOffset(targetDom, SCROLL_OFFSET);
                return true;
              } else {
                return false;
              }
            },
        };
      },
    };
  },
});
